import { useState, type ReactNode } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import StatusBar from '@/designs-dev/device/StatusBar';
import ModalScreen from '@/designs-dev/device/ModalScreen';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import AppBar from '@/karehero-app/shell/AppBar';
import Navigation from '@/karehero-app/shell/Navigation';
import Body from '@/karehero-app/shell/Body';
import Card from '@/components/wrapped/Card';
import SearchField from '@/components/mui/SearchField';
import ArticleCard from '@/karehero-app/home/ArticleCard';
import ArticleContent from '@karehero/llama/components/pages/ArticleContent/ArticleContent';
import WebinarContent from '@karehero/llama/components/pages/WebinarContent/WebinarContent';

import { HELP } from '@/karehero-app/help/Help.content';
import { helpArticle, helpWebinar } from '@/karehero-app/help/Help.fixtures';

const noop = () => {};

/** Which list is open over the tab, if any. */
export type HelpList = 'webinars' | 'articles';

/** A webinar or article opened from its card, by the card's id. */
export type HelpRead = { kind: 'webinar' | 'article'; id: string };

// A row of cards you swipe, bleeding through the body's gutter and putting it
// back as padding: the first card lines up with the heading above it and the
// last runs off the edge rather than stopping short of it.
//
// Both lists are a few of many now. A stack of either one pushed the other off
// the screen, and the tab is supposed to show that there are three ways of
// getting unstuck rather than one of them at a time.
const Carousel = ({ children }: { children: ReactNode[] }) => (
  <Box
    sx={{
      mx: -3,
      px: 3,
      pb: 0.5,
      display: 'flex',
      gap: 2,
      overflowX: 'auto',
      scrollSnapType: 'x mandatory',
    }}
  >
    {children.map((card, i) => (
      <Box
        key={(card as { key?: string }).key ?? i}
        sx={{ flex: '0 0 78%', scrollSnapAlign: 'start' }}
      >
        {card}
      </Box>
    ))}
  </Box>
);

// A section heading that is also the way into the whole list.
//
// The tab shows a few of each. Everything we have is behind the heading rather
// than behind a "See all" under the last card: the heading is what you are
// already reading when you decide you want more of this, and a link at the
// bottom is only found by the people who scrolled past everything.
const SectionHeading = ({
  title,
  onOpen,
}: {
  title: string;
  onOpen: () => void;
}) => (
  <Stack
    sx={{
      pt: 1,
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
    }}
    direction='row'
    spacing={1}
    onClick={onOpen}
  >
    <Typography variant='h3'>{title}</Typography>
    <Box sx={{ color: 'primary.main', flexShrink: 0 }}>
      <Icon icon='chevron-right' fontSize={16} />
    </Box>
  </Stack>
);

// The Help tab: a person first, then the things you can read or attend without
// asking anybody.
//
// It replaces a tab called Chat. Naming the tab after one of the three ways of
// getting unstuck made the other two hard to find — the articles were on Home,
// and webinars were nowhere — and it asked somebody who is stuck to classify
// their own problem before they could go anywhere.
const HelpScreen = ({
  onNotifications,
  onTalk,
  title,
  initialList,
  initialRead,
}: {
  onNotifications?: () => void;
  /** Opens the chat. */
  onTalk?: () => void;
  /** The person being cared for, beside the logo. */
  title?: string;
  /** Harness-only: open straight onto one of the lists, for a flows page. */
  initialList?: HelpList;
  /** Harness-only: open straight onto a webinar or article. */
  initialRead?: HelpRead;
} = {}) => {
  const [list, setList] = useState<HelpList | undefined>(initialList);
  const [read, setRead] = useState<HelpRead | undefined>(initialRead);
  // One box for both lists: only one of them is ever open, and a query typed
  // into webinars means nothing in articles.
  const [query, setQuery] = useState('');
  const open = (which: HelpList) => {
    setQuery('');
    setList(which);
  };
  const matches = (...fields: string[]) =>
    !query.trim() ||
    fields.join(' ').toLowerCase().includes(query.trim().toLowerCase());

  const webinarCard = (webinar: (typeof HELP.webinars)[number]) => (
    <Card
      key={webinar.id}
      sx={{ height: '100%' }}
      // h6, the same as the article cards under them: these are two lists of
      // the same kind of thing, and the webinars were reading a size louder for
      // no reason but the card's default.
      titleVariant='h6'
      title={webinar.title}
      subtitle={`${webinar.when} · ${webinar.summary}`}
      onClick={() => setRead({ kind: 'webinar', id: webinar.id })}
      chevron={false}
    />
  );

  const articleCard = (article: (typeof HELP.articles)[number]) => (
    <ArticleCard
      key={article.id}
      title={article.title}
      summary={article.summary}
      onClick={() => setRead({ kind: 'article', id: article.id })}
    />
  );

  // The whole list, over the tab. A page rather than a tab of its own: reading
  // is something you come back from, and the tab is where you came from.
  //
  // Search at the top, because the page exists for the times the carousel did
  // not have the thing you wanted: the list is everything we have, and a list
  // of everything is only usable if you can say what you are after.
  const page = (heading: string, placeholder: string, cards: ReactNode[]) => (
    <ModalScreen from='right' onClose={() => setList(undefined)}>
      {(close) => (
        <ScreenRoot>
          <StatusBar />
          <AppBar title={heading} align='left' onBack={close} />
          <Body>
            <Stack spacing={2}>
              <SearchField
                size='small'
                placeholder={placeholder}
                value={query}
                onChange={setQuery}
              />
              {cards.length ? (
                cards
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  {HELP.noMatches}
                </Typography>
              )}
            </Stack>
          </Body>
        </ScreenRoot>
      )}
    </ModalScreen>
  );

  return (
    <ScreenRoot>
      <StatusBar />

      <Navigation
        title={title}
        onNotifications={onNotifications}
        avatarInitials='OP'
      />

      <Body>
        <Stack spacing={2}>
          <Button
            variant='primary'
            size='lg'
            isFullWidth
            ariaLabel={HELP.talk}
            onPress={onTalk ?? noop}
          >
            {HELP.talk}
          </Button>

          {/* Above the articles because they expire: an article is there
              whenever you look, and a session you have missed is worth
              nothing. */}
          <SectionHeading
            title={HELP.webinarsTitle}
            onOpen={() => open('webinars')}
          />

          <Carousel>{HELP.webinars.map(webinarCard)}</Carousel>

          <SectionHeading
            title={HELP.articlesTitle}
            onOpen={() => open('articles')}
          />

          <Carousel>{HELP.articles.map(articleCard)}</Carousel>
        </Stack>
      </Body>

      {list === 'webinars' &&
        page(
          HELP.webinarsTitle,
          HELP.searchWebinars,
          HELP.webinars
            .filter((w) => matches(w.title, w.summary, w.when))
            .map(webinarCard),
        )}
      {list === 'articles' &&
        page(
          HELP.articlesTitle,
          HELP.searchArticles,
          HELP.articles
            .filter((a) => matches(a.title, a.summary))
            .map(articleCard),
        )}

      {/* The page a card opens: llama's own, as the app shows it. Over a list
          too, so Back lands where the card was. */}
      {read && (
        <ModalScreen from='right' onClose={() => setRead(undefined)}>
          {(close) => (
            <ScreenRoot>
              <StatusBar />
              <Body>
                {read.kind === 'webinar' ? (
                  <WebinarContent
                    content={helpWebinar(read.id)}
                    isLoading={false}
                    navigate={close as never}
                  />
                ) : (
                  <ArticleContent
                    content={helpArticle(read.id)}
                    isLoading={false}
                    onBack={close}
                  />
                )}
              </Body>
            </ScreenRoot>
          )}
        </ModalScreen>
      )}
    </ScreenRoot>
  );
};

export default HelpScreen;
